import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PayPalCheckoutButton from '../components/PayPalCheckoutButton';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight, CreditCard, Heart, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth, useCart, API, BACKEND_URL } from '../App';
import { Input } from '../components/ui/input';
import axios from 'axios';
import { getStoredUtm } from '../lib/utmTracker';
import { detectFlwCurrency } from '../lib/geoCurrency';

export default function CartPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { cart, updateCartItem, removeFromCart, clearCart, loading } = useCart();
  const [checkingOut, setCheckingOut] = useState(false);
  const [flwCurrency, setFlwCurrency] = useState('NGN');
  const [flwAutoDetected, setFlwAutoDetected] = useState(false);
  const [checkingOutFlw, setCheckingOutFlw] = useState(false);
  const [referralInput, setReferralInput] = useState('');

  // Auto-detect the buyer's likely Flutterwave currency (browser locale → IP)
  useEffect(() => {
    let alive = true;
    detectFlwCurrency('USD').then((cur) => {
      if (!alive || !cur) return;
      setFlwCurrency(cur);
      setFlwAutoDetected(true);
    });
    return () => { alive = false; };
  }, []);
  const [referralApplied, setReferralApplied] = useState(null); // { code, pastor_name }
  const [applyingReferral, setApplyingReferral] = useState(false);

  const applyReferral = async () => {
    const code = referralInput.trim().toUpperCase();
    if (!code) return;
    setApplyingReferral(true);
    try {
      const r = await axios.post(`${API}/cart/apply-referral`, { code });
      if (r.data.applied) {
        setReferralApplied({ code: r.data.code, referrer_name: r.data.referrer_name });
        toast.success(r.data.message);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid code');
      setReferralApplied(null);
    } finally {
      setApplyingReferral(false);
    }
  };

  const removeReferral = async () => {
    try {
      await axios.post(`${API}/cart/apply-referral`, { code: '' });
      setReferralApplied(null);
      setReferralInput('');
      toast.success('Referral code removed');
    } catch { /* no-op */ }
  };

  const handleQuantityChange = async (productId, newQuantity) => {
    try {
      await updateCartItem(productId, newQuantity);
    } catch (error) {
      toast.error('Failed to update quantity');
    }
  };

  const handleRemove = async (productId) => {
    try {
      await removeFromCart(productId);
      toast.success('Item removed from cart');
    } catch (error) {
      toast.error('Failed to remove item');
    }
  };

  const handleStripeCheckout = async () => {
    setCheckingOut(true);
    try {
      const utm = getStoredUtm();
      const response = await axios.post(`${API}/payments/stripe/checkout`, {
        origin_url: window.location.origin,
        payment_method: 'stripe',
        utm: utm || undefined,
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      const status = error.response?.status;
      // Friendlier message when Stripe keys are missing/misconfigured
      if (status === 503 || /not configured|stripe/i.test(detail)) {
        toast.error("We're temporarily unable to process payments. Please try again in a few minutes or contact support@christkingdomglobalmarketplace.com — your cart is saved.");
      } else {
        toast.error(detail || 'Failed to initiate checkout');
      }
      setCheckingOut(false);
    }
  };

  const handleFlutterwaveCheckout = async () => {
    setCheckingOutFlw(true);
    try {
      const utm = getStoredUtm();
      const response = await axios.post(`${API}/payments/flutterwave/checkout`, {
        origin_url: window.location.origin,
        payment_method: 'flutterwave',
        currency: flwCurrency,
        utm: utm || undefined,
      });
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to start Flutterwave checkout';
      toast.error(detail);
      setCheckingOutFlw(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center px-6">
          <div className="text-center">
            <ShoppingBag className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h1 className="text-2xl font-heading font-medium mb-4">Please Login</h1>
            <p className="text-muted-foreground mb-6">You need to be logged in to view your cart</p>
            <Link to="/login">
              <Button data-testid="login-to-view-cart">Login</Button>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 max-w-7xl mx-auto w-full px-6 md:px-12 py-8">
          <div className="animate-pulse space-y-4">
            {['c1','c2','c3'].map((k) => (
              <div key={k} className="h-24 bg-muted rounded-lg" />
            ))}
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (cart.items.length === 0) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center px-6">
          <div className="text-center">
            <ShoppingBag className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h1 className="text-2xl font-heading font-medium mb-4">Your Cart is Empty</h1>
            <p className="text-muted-foreground mb-6">Looks like you haven&apos;t added anything yet</p>
            <Link to="/products">
              <Button data-testid="continue-shopping-empty">Continue Shopping</Button>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      
      <main className="flex-1 max-w-7xl mx-auto w-full px-6 md:px-12 py-8">
        <h1 className="text-3xl font-heading font-medium mb-8" data-testid="cart-title">Shopping Cart</h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cart.items.map((item) => (
              <Card key={item.product_id} data-testid={`cart-item-${item.product_id}`}>
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    <Link to={`/products/${item.product_id}`} className="shrink-0">
                      <div className="w-24 h-24 rounded-lg overflow-hidden bg-muted">
                        <img 
                          src={item.image || 'https://images.unsplash.com/photo-1655147999706-91d0f00e7754?w=400'}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </Link>
                    <div className="flex-1 min-w-0">
                      <Link to={`/products/${item.product_id}`}>
                        <h3 className="font-medium text-foreground hover:text-primary transition-colors line-clamp-1">
                          {item.name}
                        </h3>
                      </Link>
                      <p className="text-sm text-muted-foreground">{item.vendor_name}</p>
                      {item.variant_label && (
                        <p className="text-xs text-muted-foreground mt-0.5" data-testid="cart-variant-label">
                          {item.variant_label}
                        </p>
                      )}
                      <p className="text-lg font-semibold text-primary mt-1">${item.price.toFixed(2)}</p>
                    </div>
                    <div className="flex flex-col items-end justify-between">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleRemove(item.product_id)}
                        className="text-muted-foreground hover:text-destructive"
                        data-testid={`remove-item-${item.product_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      <div className="flex items-center border border-border rounded-lg">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleQuantityChange(item.product_id, item.quantity - 1)}
                          disabled={item.quantity <= 1}
                        >
                          <Minus className="h-3 w-3" />
                        </Button>
                        <span className="w-8 text-center text-sm font-medium">{item.quantity}</span>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleQuantityChange(item.product_id, item.quantity + 1)}
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            <div className="flex justify-between pt-4">
              <Link to="/products">
                <Button variant="ghost" data-testid="continue-shopping">
                  <ArrowRight className="h-4 w-4 mr-2 rotate-180" />
                  Continue Shopping
                </Button>
              </Link>
              <Button variant="ghost" onClick={clearCart} className="text-destructive" data-testid="clear-cart">
                Clear Cart
              </Button>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24">
              <CardContent className="p-6">
                <h2 className="text-xl font-heading font-medium mb-4">Order Summary</h2>
                
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal ({cart.item_count} items)</span>
                    <span>${cart.total.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Shipping</span>
                    <span className="text-secondary">Free</span>
                  </div>
                </div>
                
                <div className="border-t border-border pt-4 mb-4">
                  {referralApplied ? (
                    <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 flex items-center gap-2 text-sm" data-testid="referral-applied">
                      <Heart className="h-4 w-4 text-primary shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-foreground font-medium">{referralApplied.code} applied</p>
                        <p className="text-xs text-muted-foreground">Supporting {referralApplied.referrer_name}</p>
                      </div>
                      <button type="button" onClick={removeReferral} className="text-xs text-muted-foreground hover:text-destructive" data-testid="referral-remove">Remove</button>
                    </div>
                  ) : (
                    <div className="space-y-2" data-testid="referral-input-block">
                      <p className="text-xs text-muted-foreground">Have a referral code? Support the person who sent you here.</p>
                      <div className="flex gap-2">
                        <Input
                          placeholder="Referral code (optional)"
                          value={referralInput}
                          onChange={(e) => setReferralInput(e.target.value.toUpperCase())}
                          className="flex-1"
                          data-testid="referral-input"
                        />
                        <Button type="button" variant="outline" onClick={applyReferral} disabled={applyingReferral || !referralInput.trim()} data-testid="referral-apply">
                          {applyingReferral ? '…' : 'Apply'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="border-t border-border pt-4 mb-6">
                  <div className="flex justify-between text-lg font-semibold">
                    <span>Total</span>
                    <span className="text-primary" data-testid="cart-total">${cart.total.toFixed(2)}</span>
                  </div>
                </div>

                <div className="space-y-3">
                  {(() => {
                    const isAfrican = flwCurrency && flwCurrency !== 'USD';
                    const stripeButton = (
                      <Button
                        key="stripe"
                        className="w-full"
                        size="lg"
                        onClick={handleStripeCheckout}
                        disabled={checkingOut || checkingOutFlw}
                        variant={isAfrican ? 'outline' : 'default'}
                        data-testid="checkout-stripe-button"
                      >
                        {checkingOut ? (
                          'Processing...'
                        ) : (
                          <>
                            <CreditCard className="h-5 w-5 mr-2" />
                            {isAfrican ? 'Or pay with Card (Stripe, USD)' : 'Pay with Card (Stripe)'}
                          </>
                        )}
                      </Button>
                    );

                    const flutterwaveBlock = (
                      <div
                        key="flw"
                        className={`rounded-lg border p-3 space-y-2 ${isAfrican ? 'border-[hsl(var(--gold))] bg-[hsl(var(--gold))]/10 ring-2 ring-[hsl(var(--gold))]/30' : 'border-[hsl(var(--gold))]/40 bg-[hsl(var(--gold))]/5'}`}
                        data-testid="flutterwave-section"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 text-xs font-semibold text-[hsl(var(--navy))]">
                            <Globe className="h-4 w-4 text-[hsl(var(--gold))]" />
                            {isAfrican ? 'Recommended for you' : 'Africa-friendly: card, mobile money, bank transfer'}
                          </div>
                          <select
                            value={flwCurrency}
                            onChange={(e) => { setFlwCurrency(e.target.value); setFlwAutoDetected(false); }}
                            className="text-xs border border-border rounded px-2 py-1 bg-white"
                            data-testid="flutterwave-currency"
                          >
                            <option value="NGN">NGN ₦</option>
                            <option value="KES">KES KSh</option>
                            <option value="GHS">GHS ₵</option>
                            <option value="ZAR">ZAR R</option>
                            <option value="USD">USD $</option>
                          </select>
                        </div>
                        {flwAutoDetected && (
                          <p className="text-[11px] text-muted-foreground flex items-center gap-1" data-testid="flutterwave-autodetect-hint">
                            <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
                            Currency auto-selected based on your location — change it any time.
                          </p>
                        )}
                        <Button
                          className="w-full bg-[hsl(var(--gold))] text-[hsl(var(--navy-dark))] hover:bg-[hsl(var(--gold))]/90 font-semibold"
                          size="lg"
                          onClick={handleFlutterwaveCheckout}
                          disabled={checkingOut || checkingOutFlw}
                          data-testid="checkout-flutterwave-button"
                        >
                          {checkingOutFlw ? (
                            'Processing...'
                          ) : (
                            <>
                              <Globe className="h-5 w-5 mr-2" />
                              Pay with Flutterwave ({flwCurrency})
                            </>
                          )}
                        </Button>
                      </div>
                    );

                    // African buyer → Flutterwave shows FIRST as primary CTA.
                    // Global (USD) buyer → Stripe first (default behavior).
                    return isAfrican ? [flutterwaveBlock, stripeButton] : [stripeButton, flutterwaveBlock];
                  })()}

                  {/* PayPal Smart Buttons — house-brand carts only (option c) */}
                  {(() => {
                    const items = cart?.items || [];
                    if (items.length === 0) return null;
                    const houseBrandOnly = items.every((it) => (it.vendor_id || 'admin') === 'admin');
                    if (!houseBrandOnly) return null;
                    return (
                      <div className="pt-1" data-testid="paypal-house-brand-block">
                        <div className="flex items-center gap-2 text-[11px] text-muted-foreground mb-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-500 inline-block" />
                          <span>PayPal available — this is a Christ Kingdom house-brand order</span>
                        </div>
                        <PayPalCheckoutButton
                          disabled={checkingOut || checkingOutFlw}
                          onSuccess={(orderId) => {
                            // Cart is cleared server-side on genuine (non-sandbox) capture.
                            // Client refreshes its view from server on next fetch, so no local clearCart() here.
                            navigate(`/checkout/success?order_id=${orderId}&method=paypal`);
                          }}
                        />
                      </div>
                    );
                  })()}
                  
                  {/* Buyer trust signals */}
                  <div className="bg-muted/40 border border-border rounded-lg p-3 space-y-1.5" data-testid="buyer-trust-signals">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <svg className="h-3.5 w-3.5 text-green-600 shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                      <span><strong>256-bit encryption</strong> — your card data is never stored on our servers</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <svg className="h-3.5 w-3.5 text-green-600 shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                      <span><strong>Buyer protection</strong> — full refund if item not as described</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <svg className="h-3.5 w-3.5 text-green-600 shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                      <span><strong>Verified vendors</strong> — KYC-approved Christian businesses</span>
                    </div>
                  </div>

                  <p className="text-xs text-center text-muted-foreground">
                    Secured by Stripe & Flutterwave. PayPal coming soon.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
